import { TenantDefaultMargins } from '@core/types/tenantDefaultMargins';

import { Cost } from '../types/api/Cost/Cost';
import { Currency, NewCurrency } from '../types/api/Currency/Currency';
import type { DeliveryCost } from '../types/api/DeliveryCost/DeliveryCost';
import Enums from '../types/api/Enums';
import type { Product } from '../types/api/Product/Product';
import { ReadyMixConstants } from '../types/api/ReadyMixConstants/ReadyMixConstants';
import { CurrencyString } from './currency';
import { resolveCurrencyCode } from './format';
import { FormatMarginCurrency } from './marginCalculations';
import { convertUnits } from './unitHelpers';

type CalculateOtherCostParams = {
  product: Product;
  otherCostOverride?: Currency | null;
  deliveryCosts?: DeliveryCost | null;
};

/**
 * Rebases a Cost onto `toUnit`. Each Cost here is a rate ($ per its own unit), so it converts in
 * the opposite direction of a quantity conversion. Units in different categories have no
 * conversion factor (e.g. a per-hour or per-load Mix against $/cu_yd constants); those keep the
 * raw amount rather than dropping it, matching public.calculate_other_cost_amount.
 */
const CostPerUnit = (cost: Cost, toUnit: Enums.Unit): Currency => {
  const amount = cost.stripCommas().amount;
  const factor = convertUnits(1, toUnit, cost.unit) ?? 1;
  if (factor === 1) {
    // Return untouched so same-unit costs keep their precision instead of gaining decimals.
    return amount;
  }
  return amount.multiply(factor) ?? Currency.zero();
};

/**
 * Combines the specified delivery costs with the fixed costs of the product's plant to calculate the non-material
 * cost for a product, each converted to the product's own quantity unit.
 *
 * The other cost calculation is only defined for Mix products. Otherwise, this simply returns
 * otherCostOverride (if provided, otherwise null).
 *
 * Mirrors public.calculate_other_cost_amount — keep both in sync.
 */
export const CalculateOtherCost = ({
  product,
  deliveryCosts = null,
  otherCostOverride = product.otherCostOverride,
}: CalculateOtherCostParams): Currency | null => {
  if (otherCostOverride !== null) {
    return otherCostOverride;
  }

  if (product.category !== Enums.ProductCategory.ReadyMix) {
    return null;
  }

  // Missing constants count as zero and still let the delivery cost through, matching the SQL.
  const readyMixConstants = product.plant.readyMixConstants ?? ReadyMixConstants.zero();
  const { quantityUnit } = product;
  return CostPerUnit(readyMixConstants.operatingCost, quantityUnit).add(
    CostPerUnit(readyMixConstants.sgaCost, quantityUnit),
    CostPerUnit(deliveryCosts?.totalDeliveryCost ?? Cost.zero(), quantityUnit),
  );
};

/**
 * Calculates a margin in dollars given a cost and margin percentage (target or minimum - this is
 * agnostic to which). This uses the same logic as CalculateSuggestedPrice but returns just the margin amount.
 */
export const CalculateMarginDollars = (totalCost: number, marginPercent: number): number | null => {
  if (totalCost <= 0 || marginPercent > 1 || marginPercent < 0) {
    return null;
  }

  // Suggested price = cost / (1 - margin), so margin dollars = suggested price - cost
  const suggestedPrice = totalCost / (1 - marginPercent);
  return suggestedPrice - totalCost;
};

/**
 * Returns the product's explicitly configured flat-dollar target margin override (product, plant, or tenant
 * level, in that priority order), or null if none is configured. These aren't cost-derived, so callers should
 * use the override as-is rather than recomputing it from a cost basis.
 *
 * Only the tenant default needs a currency attached here — the product/plant overrides already carry one.
 */
const ResolveTargetMarginFlatOverride = (
  product: Product,
  defaultMargins: TenantDefaultMargins,
): Currency | null => {
  if (product.targetMarginFlatAmountOverride !== null) {
    return product.targetMarginFlatAmountOverride;
  }

  const plantOverride = product.plant.readyMixConstants?.targetMarginFlatAmountOverride;
  if (plantOverride !== null && plantOverride !== undefined) {
    return plantOverride;
  }

  const tenantDefault = defaultMargins.targetMarginFlatAmount;
  if (
    tenantDefault !== null &&
    tenantDefault !== undefined &&
    tenantDefault !== '' &&
    tenantDefault !== '0'
  ) {
    const currency = resolveCurrencyCode(product.plant.currencyCode);
    return NewCurrency({ number: tenantDefault, currency });
  }

  return null;
};

/**
 * Formats a line item's target margin in dollars. Uses the product's explicitly configured flat-amount
 * override when present (product, plant, or tenant level); otherwise derives the amount from that line
 * item's own total cost (e.g. from QuoteProduct.costPerUnit()/ProjectProduct.costPerUnit()).
 */
export const CalculateTargetMarginCurrency = ({
  product,
  totalCost,
  defaultMargins,
  currencyCode,
}: {
  product: Product;
  totalCost: number;
  defaultMargins: TenantDefaultMargins;
  currencyCode?: string | null;
}): string | null => {
  const flatOverride = ResolveTargetMarginFlatOverride(product, defaultMargins);
  if (flatOverride !== null) {
    return CurrencyString({ cur: flatOverride, currencyCode });
  }

  const targetMarginPercentage = product.targetMarginPercentage(defaultMargins);
  if (targetMarginPercentage === null) {
    return null;
  }
  const targetMarginDollars = CalculateMarginDollars(totalCost, targetMarginPercentage / 100);
  return FormatMarginCurrency(targetMarginDollars, currencyCode);
};

/**
 * Calculates a flat-dollar target margin amount. Respects the product's explicitly configured
 * flat-amount override (product, plant, or tenant level) when present; otherwise $0, since the
 * flat-dollar and percentage margin settings are independent and one is never derived from the other.
 */
export const CalculateTargetMarginFlatAmount = ({
  product,
  defaultMargins,
}: {
  product: Product;
  defaultMargins: TenantDefaultMargins;
}): Currency => {
  return ResolveTargetMarginFlatOverride(product, defaultMargins) ?? Currency.zero();
};

/**
 * Returns the product's explicitly configured flat-dollar minimum margin override (product, plant, or tenant
 * level, in that priority order), or null if none is configured. These aren't cost-derived, so callers should
 * use the override as-is rather than recomputing it from a cost basis.
 *
 * Only the tenant default needs a currency attached here — the product/plant overrides already carry one.
 */
const ResolveMinimumMarginFlatOverride = (
  product: Product,
  defaultMargins: TenantDefaultMargins,
): Currency | null => {
  if (product.minimumMarginFlatAmountOverride !== null) {
    return product.minimumMarginFlatAmountOverride;
  }

  const plantOverride = product.plant.readyMixConstants?.minimumMarginFlatAmountOverride;
  if (plantOverride !== null && plantOverride !== undefined) {
    return plantOverride;
  }

  const tenantDefault = defaultMargins.minimumMarginFlatAmount;
  if (
    tenantDefault !== null &&
    tenantDefault !== undefined &&
    tenantDefault !== '' &&
    tenantDefault !== '0'
  ) {
    const currency = resolveCurrencyCode(product.plant.currencyCode);
    return NewCurrency({ number: tenantDefault, currency });
  }

  return null;
};

/**
 * Calculates a flat-dollar minimum margin amount. Respects the product's explicitly configured
 * flat-amount override (product, plant, or tenant level) when present; otherwise $0, since the
 * flat-dollar and percentage margin settings are independent and one is never derived from the other.
 */
export const CalculateMinimumMarginFlatAmount = ({
  product,
  defaultMargins,
}: {
  product: Product;
  defaultMargins: TenantDefaultMargins;
}): Currency => {
  return ResolveMinimumMarginFlatOverride(product, defaultMargins) ?? Currency.zero();
};

/**
 * Combines a product's material and other costs with the specified delivery costs and the product's target margin to
 * calculate a suggested price for the product.
 */
export const CalculateSuggestedPrice = ({
  product,
  deliveryCosts = null,
  otherCostOverride = product.otherCostOverride,
  marginConfiguration = Enums.MarginConfiguration.Percentage,
  defaultMargins,
}: CalculateOtherCostParams & {
  marginConfiguration?: Enums.MarginConfiguration;
  defaultMargins: TenantDefaultMargins;
}): Currency | null => {
  const materialCost =
    product.materialCostOverride ?? product.suggestedMaterialCostCurrency() ?? Currency.zero();
  const otherCost = CalculateOtherCost({ product, deliveryCosts, otherCostOverride });
  const totalCost = otherCost === null ? materialCost : materialCost.add(otherCost);
  if (marginConfiguration === Enums.MarginConfiguration.Percentage) {
    const targetMargin = product.targetMarginPercentage(defaultMargins);
    // Treat missing/invalid percentage as 0% margin (i.e., suggested price == total cost)
    if (targetMargin === null || Number.isNaN(targetMargin) || targetMargin <= 0) {
      return totalCost?.multiply(1) ?? Currency.zero();
    }
    // 100% (or greater) margin is undefined → blank result
    if (targetMargin >= 100) {
      return null;
    }
    return totalCost?.divide(1 - targetMargin / 100) ?? null;
  } else {
    const targetMarginFlatAmount = CalculateTargetMarginFlatAmount({
      product,
      defaultMargins,
    });
    return materialCost.add(otherCost ?? Currency.zero())?.add(targetMarginFlatAmount) ?? null;
  }
};
